import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import PageIcon from '@atlaskit/icon/core/page';
import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import VideoIcon from '@atlaskit/icon/core/video';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { useFetchSmartLinkData } from '../../../common/controller/use-fetch-smartlink-data/useFetchSmartLinkData';
import {
	type ListElement,
	type SmartLinkElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';
import { messages } from './messages';

interface DynamicLayoutListItemProps {
	itemData: any;
}

const styles = cssMap({
	documentWrapper: {
		position: 'relative',
		'&:hover': {
			backgroundColor: token('color.background.input.hovered'),
			borderRadius: token('radius.small'),
		},
	},
	invisibleLinkWrapper: {
		position: 'absolute',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		backgroundImage: 'none',
		backgroundColor: 'transparent',
		appearance: 'none',
		padding: '0',
		zIndex: 1,
	},
	pageIconWrapper: {
		height: '32px',
		width: '32px',
		boxSizing: 'border-box',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
	documentMetadataIconWrapper: {
		marginTop: token('space.negative.025'),
		height: '12px',
		width: '12px',
	},
	listDocumentWrapper: {
		width: '100%',
	},
	showMoreButtonWrapper: {
		width: '88px',
	},
});

const MAX_VISIBLE_DOCS = 3;

const InvisibleLink = ({ name, url }: { name: string; url: string }) => (
	// eslint-disable-next-line no-restricted-syntax
	<Anchor href={url} xcss={styles.invisibleLinkWrapper} aria-label={name} tabIndex={-1} />
);

const getDocumentIcon = (itemData: any) => {
	if (itemData.url && itemData.url.includes('docs.google.com')) {
		return <FileIcon color={token('color.icon.success')} label="" />;
	}
	if (itemData.url && itemData.url.includes('loom.com')) {
		return <VideoIcon color={token('color.icon.discovery')} label="" />;
	}
	if (
		itemData.url &&
		(itemData.url.includes('bitbucket.org') || itemData.url.includes('github.com'))
	) {
		return <PullRequestIcon color={token('color.icon.accent.green')} label="" />;
	}
	return <PageIcon color={token('color.icon.brand')} label="" />;
};

const DynamicLayoutListItem = ({ itemData }: DynamicLayoutListItemProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={styles.documentWrapper}>
			<InvisibleLink name={itemData.name} url={itemData.url} />
			<Inline space="space.150" alignBlock="center">
				<Box xcss={styles.pageIconWrapper}>{getDocumentIcon(itemData)}</Box>
				<Stack space="space.050">
					<Inline space="space.050" alignBlock="center">
						<Text size="large" maxLines={1}>
							{itemData.name}
						</Text>
						{itemData['atlassian:state'] === 'MERGED' && (
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
							<Lozenge appearance="success" isBold>
								{formatMessage(messages.dynamic_layout_list_project_status_merged)}
							</Lozenge>
						)}
					</Inline>
					{itemData.generator && (
						<Inline space="space.050">
							<Box xcss={styles.documentMetadataIconWrapper}>
								<Image src={itemData.generator.icon?.url} alt="" />
							</Box>
							<Text color="color.text.subtle" size="small" maxLines={1}>
								{itemData.generator.name}
							</Text>
							<Text color="color.text.subtle" size="small">
								•
							</Text>
							<Text color="color.text.subtle" size="small">
								{itemData.attributedTo?.name}
							</Text>
						</Inline>
					)}
				</Stack>
			</Inline>
		</Box>
	);
};

export const DynamicLayoutList = ({
	component,
}: {
	component: ListElement;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [listExpanded, setListExpanded] = useState(false);

	const listChildrenUrls = useMemo(
		() =>
			((component.children || []) as SmartLinkElement[])
				.map((listChild) => listChild.attributes?.url || '')
				.filter((url) => !!url),
		[component.children],
	);

	const { objectResolverData } = useFetchSmartLinkData({ urls: listChildrenUrls });

	const showMoreButtonVisible = objectResolverData.length > MAX_VISIBLE_DOCS;
	const numVisibleDocs = listExpanded ? objectResolverData.length : MAX_VISIBLE_DOCS;

	return objectResolverData.length ? (
		<Box xcss={styles.listDocumentWrapper} testId="dynamic-layout-list">
			<Stack space="space.200">
				{objectResolverData.slice(0, numVisibleDocs).map((data, idx) => (
					<DynamicLayoutListItem itemData={data} key={idx} />
				))}
				{showMoreButtonVisible && (
					<Box xcss={styles.showMoreButtonWrapper}>
						<Button onClick={() => setListExpanded(!listExpanded)} spacing="compact">
							{listExpanded ? (
								<Text>{formatMessage(messages.dynamic_layout_list_see_less)}</Text>
							) : (
								<Text>{formatMessage(messages.dynamic_layout_list_see_more)}</Text>
							)}
						</Button>
					</Box>
				)}
			</Stack>
		</Box>
	) : null;
};
